// Home and work, as a station and a walking radius. Nothing more.
//
// The app never learns where anybody lives. "Home" here is a station and the
// number of minutes somebody is willing to walk from it, which is enough to
// answer the only question this feature asks: when the line through Carnegie
// is down, which OTHER station is close enough to walk from. An address would
// need geocoding, a network call and a row in somebody's storage that is worth
// stealing; a stop id and the number 20 are none of those things.
//
// It is also what makes the push safe. The server is sent a headline and a
// list of route ids and never a place, so the sweep can paste "Try the Glen
// Waverley line to East Malvern" into an alert without ever having been told
// what East Malvern is near.
//
// Pure. It reads what it is handed and hands back a shape; the preference
// store, the city in force and the Vue refs are all somebody else's job.

/**
 * Where the two places live, per city.
 *
 * ALWAYS SUFFIXED, including in Melbourne. The bare-key exception exists for
 * keys that already have values on somebody's phone — see `destinationsKey` in
 * `directions-state.js` — and nobody has a home station saved yet, so this one
 * starts life with the city in it and never has to grow a migration.
 *
 * A station in Sydney is not a place in Melbourne: reading one city's home
 * station after a switch would plan a walk across the Tasman.
 */
export const PLACES_KEY = 'lateagain:places:v1';

/** `lateagain:places:v1:melbourne`, and the same shape everywhere else. */
export function placesKey(city) {
  return `${PLACES_KEY}:${city}`;
}

/**
 * How somebody travels, which is about the person rather than about the city.
 *
 * Not suffixed, deliberately. Somebody who will not get on a replacement bus
 * in Melbourne will not get on one in Sydney either, and asking them twice
 * would be the app forgetting something it was told.
 */
export const TRAVEL_KEY = 'lateagain:travel:v1';

/**
 * The three answers to "how far will you walk?".
 *
 * Three, not a slider. A slider asks for a number nobody has; three chips ask
 * a question somebody can answer standing at a barrier, and the difference
 * between 18 and 22 minutes was never going to change which train they catch.
 */
export const WALK_RADII = [10, 20, 30];

/**
 * A walking pace, in metres per minute.
 *
 * 80 m/min is about 4.8 km/h — an ordinary adult walking somewhere on purpose,
 * not a stroll and not a run. It is used to turn "I'll walk 20 minutes" into a
 * circle on the map, so being a little slow is the safe direction to be wrong
 * in: a radius that is too small offers fewer stations, and one that is too
 * big offers a walk somebody will not do.
 */
export const WALK_METRES_PER_MINUTE = 80;

/**
 * The furthest the planner will walk anybody to a station, in metres.
 *
 * About twenty-five minutes at `WALK_METRES_PER_MINUTE`, and the same figure on
 * the phone (how far a place's radius reaches) and on the server (which
 * stations the planner will start from). The longest chip says thirty minutes,
 * which is 2.4 km on paper; two kilometres is where "I'll walk it" becomes "I
 * will take a bus", and past it the planner would be offering a walk to a
 * station that is really another town's: Canberra station is 4.9 km from the
 * nearest light rail stop, and a walk of that length is not a connection.
 */
export const MAX_WALK_METRES = 2000;

/** How far this place's radius reaches, in metres, or 0 for no place. */
export function radiusMetres(place) {
  const minutes = Number(place?.walkMinutes);
  if (!WALK_RADII.includes(minutes)) return 0;
  return Math.min(minutes * WALK_METRES_PER_MINUTE, MAX_WALK_METRES);
}

/** One slot, or null where it is missing, half written or out of range. */
function readPlace(slot) {
  const stopId = Number(slot?.stopId);
  const walkMinutes = Number(slot?.walkMinutes);
  if (!Number.isFinite(stopId) || stopId <= 0) return null;
  if (!WALK_RADII.includes(walkMinutes)) return null;
  return { stopId, walkMinutes };
}

/** Whatever is on the phone, read as `{ home, work }` with bad slots null. */
export function readPlaces(raw) {
  const held = parsed(raw);
  return {
    home: readPlace(held?.home),
    work: readPlace(held?.work),
  };
}

/** How the person travels, with the defaults filled in. */
export function readTravel(raw) {
  const held = parsed(raw);
  const buses = held?.replacementBuses;
  return {
    replacementBuses: buses === 'avoid' ? 'avoid' : 'ok',
  };
}

/**
 * The stored value as an object, whether it arrived as one or as JSON text.
 *
 * `prefs` parses on the way out, so the object is the normal case; a caller
 * holding a raw string — a test, a migration, a payload off the wire — should
 * not have to know that.
 */
function parsed(raw) {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw && typeof raw === 'object' ? raw : null;
}
